/* eslint-disable react/jsx-key */
import clsx from "clsx";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useTheme } from "@mui/material";
import styles from "./TechnicalSentimentChart.module.scss";

const cx = 150;
const cy = 100;
const iR = 88;
const oR = 100;

const sectors = [
    { value: 40, color: "#F25555" },
    { value: 40, color: "#F25555" },
    { value: 40, color: "url(#segmentGradient)" },
    { value: 40, color: "#00C853" },
    { value: 40, color: "#00C853" },
];

const needleBaseRadiusPx = 5;
const needleLength = 72;

const getNeedleLabel = (value: string) => {
    if (value === "StongSell" || value === "StrongSell") return "Strong Sell";
    if (value === "Sell") return "Sell";
    if (value === "Neutral") return "Neutral";
    if (value === "Buy") return "Buy";
    if (value === "StrongBuy") return "Strong Buy";
};

const Needle = ({
    cx,
    cy,
    angle,
}: {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    angle: number;
}) => {
    const { palette } = useTheme();
    const needleColor = palette.mode === "dark" ? "#d9d9d9" : "#343841";
    const needleBaseCenterX = cx;
    const needleBaseCenterY = cy;

    return (
        <g>
            <circle
                cx={needleBaseCenterX}
                cy={needleBaseCenterY}
                r={needleBaseRadiusPx}
                fill={needleColor}
                stroke="none"
            />
            <path
                d={`M${needleBaseCenterX},${needleBaseCenterY}l${needleLength},0`}
                strokeWidth={2}
                stroke={needleColor}
                fill={needleColor}
                style={{
                    transform: `rotate(-${angle}deg)`,
                    transformOrigin: `${needleBaseCenterX}px ${needleBaseCenterY}px`,
                }}
            />
        </g>
    );
};

const calculateAngle = (buy: number, neutral: number, sell: number) => {
    const total = buy + neutral + sell;

    if (total === 0) return 90;

    const index = (buy - sell) / total;
    const angle = ((index + 1) / 2) * 180;
    return 180 - Math.round(angle);
};

export const TechnicalSentimentChart = ({
    title,
    data,
}: {
    title: string;
    data: { buy: number; neutral: number; sell: number; scoreScale: string };
}) => {
    const { palette } = useTheme();
    if (!data) return null;

    const { buy, neutral, sell, scoreScale } = data;
    const needleAngle = calculateAngle(buy, neutral, sell);
    const needleLabel = getNeedleLabel(scoreScale);

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>{title}</h2>
            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={225}>
                    <PieChart>
                        <defs>
                            <linearGradient id="segmentGradient">
                                <stop offset="0%" stopColor="#F25555" />
                                <stop offset="100%" stopColor="#16BB6F" />
                            </linearGradient>
                        </defs>
                        <Pie
                            data={sectors}
                            startAngle={180}
                            endAngle={0}
                            cx={cx}
                            cy={cy}
                            innerRadius={iR}
                            outerRadius={oR}
                            dataKey="value"
                            stroke="none"
                            paddingAngle={4}>
                            {sectors.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Pie
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            data={sectors}
                            cx={cx}
                            cy={cy}
                            innerRadius={iR}
                            outerRadius={oR}
                            stroke="none"
                            fill="none"
                            activeIndex={1}
                            activeShape={props => <Needle {...props} angle={needleAngle} />}
                        />
                    </PieChart>
                </ResponsiveContainer>
                <div className={styles.indicators}>
                    <span className={styles.chartLabel}>
                        Strong
                        <br />
                        Sell
                    </span>
                    <span
                        className={clsx(styles.needleLabel, {
                            [styles.neutral]: needleLabel === "Neutral",
                            [styles.sell]: needleLabel === "Sell" || needleLabel === "Strong Sell",
                        })}>
                        {needleLabel}
                    </span>
                    <span className={styles.chartLabel}>
                        Strong
                        <br />
                        Buy
                    </span>
                </div>
            </div>
            <div className={styles.scoreInfo}>
                <div className={styles.item}>
                    <div className={styles.red}></div>
                    <div className={styles.label}>
                        Bearish
                        <span
                            className={clsx(null, {
                                [styles[palette.mode]]: palette.mode === "dark",
                            })}>
                            {data?.sell}
                        </span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.yellow}></div>
                    <div className={styles.label}>
                        Neutral
                        <span
                            className={clsx(null, {
                                [styles[palette.mode]]: palette.mode === "dark",
                            })}>
                            {data?.neutral}
                        </span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.green}></div>
                    <div className={styles.label}>
                        Bullish
                        <span
                            className={clsx(null, {
                                [styles[palette.mode]]: palette.mode === "dark",
                            })}>
                            {data?.buy}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
